import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useColorContext } from '../../../context/colorContext';
import BrandLogo from '../../../components/ui/BrandLogo';
import { LuMail, LuMessageCircle, LuArrowLeft } from 'react-icons/lu';

const toSafeHex = (hex, fallback) => {
  const value = String(hex || '').trim();
  return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback) => {
  const safeHex = toSafeHex(hex, fallback);
  const r = parseInt(safeHex.slice(1, 3), 16);
  const g = parseInt(safeHex.slice(3, 5), 16);
  const b = parseInt(safeHex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export default function SupportPage() {
  const navigate = useNavigate();
  const { bgPrimary, bgSecondary, textPrimary, textSecondary, activeAccent, varkColors } = useColorContext();

  const safeBgPrimary = toSafeHex(bgPrimary, '#0A0A0F');
  const safeBgSecondary = toSafeHex(bgSecondary, '#13131A');
  const safeTextPrimary = toSafeHex(textPrimary, '#FFFFFF');
  const safeTextSecondary = toSafeHex(textSecondary, '#A1A1AA');
  const safeAccent = toSafeHex(activeAccent, '#6366F1');
  const visualColor = toSafeHex(varkColors?.visual, '#7C3AED');
  const auditoryColor = toSafeHex(varkColors?.auditory, '#3B82F6');
  const whatsappColor = '#25D366';

  const contacts = [
    {
      key: 'email',
      icon: LuMail,
      label: 'Email',
      description: 'Send us a message and we\'ll get back to you within 24 hours.',
      action: 'plynote.sender@gmail.com',
      href: 'mailto:plynote.sender@gmail.com',
      color: safeAccent,
      cta: 'Send an email',
    },
    {
      key: 'whatsapp',
      icon: LuMessageCircle,
      label: 'WhatsApp',
      description: 'Chat with us directly on WhatsApp for a faster response.',
      action: '+33 7 87 32 78 24',
      href: 'https://wa.me/33787327824',
      color: whatsappColor,
      cta: 'Open WhatsApp',
    },
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${safeBgPrimary} 0%, ${hexToRgba(visualColor, 0.05, safeBgPrimary)} 20%, ${hexToRgba(auditoryColor, 0.05, safeBgPrimary)} 45%, ${hexToRgba(safeAccent, 0.06, safeBgPrimary)} 70%, ${safeBgPrimary} 100%)`,
        color: safeTextPrimary,
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Helmet>
        <title>Support — Plynote</title>
        <meta name="description" content="Get help from the Plynote team. Contact us by email or WhatsApp — we respond within 24h by email and 2h on WhatsApp." />
        <link rel="canonical" href="https://www.plynote.io/support" />
      </Helmet>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&display=swap');
        body { overflow-x: hidden; }
        @keyframes supportFadeUp {
          from { opacity: 0; transform: translateY(16px); filter: blur(4px); }
          to   { opacity: 1; transform: translateY(0);    filter: blur(0);   }
        }
      `}</style>

      {/* Header */}
      <div
        style={{
          padding: '28px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <BrandLogo size={20} />
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: hexToRgba(safeBgSecondary, 0.6, safeBgSecondary),
            border: `1px solid ${hexToRgba(safeAccent, 0.15, safeAccent)}`,
            borderRadius: 999,
            color: safeTextSecondary,
            fontSize: '0.88rem',
            fontWeight: 500,
            padding: '8px 16px',
            cursor: 'pointer',
            transition: 'color 200ms, border-color 200ms',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = safeTextPrimary;
            e.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.35, safeAccent);
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = safeTextSecondary;
            e.currentTarget.style.borderColor = hexToRgba(safeAccent, 0.15, safeAccent);
          }}
        >
          <LuArrowLeft size={15} />
          Back
        </button>
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          maxWidth: 720,
          margin: '0 auto',
          padding: '48px 24px 80px',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: 64, animation: 'supportFadeUp 600ms ease both' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 16px',
              borderRadius: 999,
              background: hexToRgba(safeAccent, 0.1, safeAccent),
              border: `1px solid ${hexToRgba(safeAccent, 0.2, safeAccent)}`,
              color: safeAccent,
              fontSize: '0.8rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: 24,
            }}
          >
            Support
          </div>
          <h1
            style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontWeight: 800,
              lineHeight: 1.1,
              marginBottom: 16,
              background: `linear-gradient(135deg, ${safeTextPrimary} 0%, ${hexToRgba(safeAccent, 0.85, safeAccent)} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            We're here to help
          </h1>
          <p
            style={{
              fontSize: '1.05rem',
              color: safeTextSecondary,
              lineHeight: 1.6,
              maxWidth: 480,
              margin: '0 auto',
            }}
          >
            Choose your preferred channel and our team will respond as quickly as possible.
          </p>
        </div>

        {/* Contact cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {contacts.map((contact, i) => {
            const Icon = contact.icon;
            return (
              <div
                key={contact.key}
                style={{
                  padding: '28px 32px',
                  background: `linear-gradient(135deg, ${hexToRgba(safeBgSecondary, 0.7, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.5, safeBgPrimary)} 100%)`,
                  border: `1px solid ${hexToRgba(contact.color, 0.15, contact.color)}`,
                  borderRadius: 24,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 24,
                  animation: `supportFadeUp 600ms ${100 + i * 120}ms ease both`,
                  transition: 'border-color 220ms, box-shadow 220ms',
                  flexWrap: 'wrap',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = hexToRgba(contact.color, 0.4, contact.color);
                  e.currentTarget.style.boxShadow = `0 12px 32px ${hexToRgba(contact.color, 0.08, contact.color)}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = hexToRgba(contact.color, 0.15, contact.color);
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Icon */}
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    background: hexToRgba(contact.color, 0.12, contact.color),
                    border: `1px solid ${hexToRgba(contact.color, 0.2, contact.color)}`,
                    display: 'grid',
                    placeItems: 'center',
                    color: contact.color,
                    flexShrink: 0,
                  }}
                >
                  <Icon size={24} />
                </div>

                {/* Text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '1.05rem', marginBottom: 4 }}>
                    {contact.label}
                  </div>
                  <div style={{ color: safeTextSecondary, fontSize: '0.9rem', lineHeight: 1.5, marginBottom: 6 }}>
                    {contact.description}
                  </div>
                  <div style={{ color: hexToRgba(contact.color, 0.85, contact.color), fontSize: '0.88rem', fontWeight: 500 }}>
                    {contact.action}
                  </div>
                </div>

                {/* CTA button */}
                <a
                  href={contact.href}
                  target={contact.key === 'whatsapp' ? '_blank' : undefined}
                  rel={contact.key === 'whatsapp' ? 'noopener noreferrer' : undefined}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '11px 22px',
                    borderRadius: 999,
                    background: `linear-gradient(135deg, ${contact.color} 0%, ${hexToRgba(contact.color, 0.75, contact.color)} 100%)`,
                    color: '#FFFFFF',
                    fontWeight: 600,
                    fontSize: '0.88rem',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    boxShadow: `0 8px 20px ${hexToRgba(contact.color, 0.25, contact.color)}`,
                    transition: 'filter 200ms, box-shadow 200ms',
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.filter = 'brightness(1.1)';
                    e.currentTarget.style.boxShadow = `0 12px 28px ${hexToRgba(contact.color, 0.38, contact.color)}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.filter = 'none';
                    e.currentTarget.style.boxShadow = `0 8px 20px ${hexToRgba(contact.color, 0.25, contact.color)}`;
                  }}
                >
                  <Icon size={15} />
                  {contact.cta}
                </a>
              </div>
            );
          })}
        </div>

        {/* Response time note */}
        <p
          style={{
            textAlign: 'center',
            color: safeTextSecondary,
            fontSize: '0.85rem',
            marginTop: 40,
            animation: 'supportFadeUp 600ms 360ms ease both',
          }}
        >
          Typical response time: under 24h by email · under 2h on WhatsApp
        </p>
      </div>

      {/* Footer */}
      <div
        style={{
          textAlign: 'center',
          padding: '24px',
          color: safeTextSecondary,
          fontSize: '0.82rem',
          borderTop: `1px solid ${hexToRgba(safeAccent, 0.08, safeAccent)}`,
        }}
      >
        © 2026 Plynote.
      </div>
    </div>
  );
}
